import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER, Injector, PLATFORM_ID, ErrorHandler } from '@angular/core';
import { TopicTypeService } from './products/products-type-setting/topic-type.service';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClient, HttpClientJsonpModule, HttpClientModule } from '@angular/common/http';
import { httpInterceptorProviders, ChunkLoadErrorHandler } from './shared/inteceptors';
import { MatPasswordStrengthModule } from '@angular-material-extensions/password-strength';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from '../environments/environment';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { LoadingService } from './shared/components/loading/loading.service';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { TransferHttpCacheModule } from '@nguniversal/common';
import { MsalModule, MsalService, MSAL_INSTANCE } from '@azure/msal-angular';
import { AppMsalConfig } from 'src/app-msal-config';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS_FACTORY } from '@angular/material-moment-adapter';
import { MatNativeDateModule, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter } from 'angular-calendar';
import { DatePipe, isPlatformBrowser, registerLocaleData } from '@angular/common';
import localeAr from '@angular/common/locales/ar';
import localeFr from '@angular/common/locales/fr';
import { httpInterceptorProvidersForActivity } from './activities/shared/inteceptors/index-activity';
import { CustomDomainService } from './shared/services/custom-domain.service';
import { IframeModule } from './iframe/iframe.module';
import { StemexeClientService } from './data-providers/services/stemexe-client.service';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { provideAppCheck, initializeAppCheck, ReCaptchaV3Provider } from '@angular/fire/app-check';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { NgProgressModule } from 'ngx-progressbar';
import { NgProgressHttpModule } from 'ngx-progressbar/http';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { NgxVcardModule } from 'ngx-vcard';
import { AlertModule } from './shared/alert/alert.module';
import { AppAppearanceService } from './shared/services/app-appearance.service';
import { CustomDateAdapter } from '@plannexe/shared/directives/custom-date-adapter';
import { CacheRouteReuseStrategy } from './shared/services/cache-route-reuse.strategy';
import { RouteReuseStrategy } from '@angular/router';
import { MembershipBlockedDialogComponent } from './shared/components/membership-blocked-dialog/membership-blocked-dialog.component'

declare const VERSION: string;
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(
    http,
    './assets/i18n/',
    `.json?v=${VERSION}`
  );
}

export function loadAppConfig(customDomainService: CustomDomainService, appearanceService: AppAppearanceService) {
  return (): Promise<any> => {
    let config = window['ENV_CONFIG'];
    if (config) {
      Object.keys(config).forEach(key => {
        environment[key] = config[key];
      })
      appearanceService.configureThemeColorsFromConfig();
    }

    // console.log("Step 1. Calling default theme color.")
    return customDomainService.Init().then(_ => appearanceService.setDefaultThemeColor());
  }
}


export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: AppMsalConfig.clientId,
      redirectUri: `${window.location.origin}/`,
    },
    cache: {
      cacheLocation: 'localStorage'
    }
  })

}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    TransferHttpCacheModule,
    HttpClientModule,
    HttpClientJsonpModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    AlertModule,
    NgxVcardModule,
    NgProgressModule,
    MembershipBlockedDialogComponent,
    NgProgressHttpModule.withConfig({
      silentApis: []
    }),
    MatPasswordStrengthModule.forRoot(),
    // SortablejsModule.forRoot({
    //  animation: 150
    // }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),

    MsalModule.forRoot(
      new PublicClientApplication({ // MSAL Configuration
        auth: {
          clientId: AppMsalConfig.clientId,
          authority: AppMsalConfig.authority,
          redirectUri: `${window.location.origin}/`,
        },
        cache: {
          cacheLocation: BrowserCacheLocation.LocalStorage,
          storeAuthStateInCookie: false, // set to true for IE 11
        },
        system: {
          loggerOptions: {
            loggerCallback: () => { },
            piiLoggingEnabled: false
          }
        }
      }), {
      interactionType: InteractionType.Redirect, // MSAL Guard Configuration
    }, {
      protectedResourceMap: new Map([
        ['http://localhost:8200/', ['api://442edcf7-9e0c-469b-93fb-daa1839724bd/acces_as_user/Acces-user']],
        ['https://ai-display-dev-pcq-dev.bravo-apps.volvocars.biz/', ['api://442edcf7-9e0c-469b-93fb-daa1839724bd/acces_as_user/Acces-user']]
      ]),
      interactionType: InteractionType.Redirect // MSAL Interceptor Configuration
    }
    ),

    IframeModule,
    provideMessaging(() => getMessaging()),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAppCheck((i: Injector) => {
      if (environment?.enableAppCheck && isPlatformBrowser(i.get(PLATFORM_ID))) {
        const provider = new ReCaptchaV3Provider(environment.recaptcha3SiteKey);
        return initializeAppCheck(undefined, { provider, isTokenAutoRefreshEnabled: true });
      }
    })    
  ],
  providers: [
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    StemexeClientService,
    CustomDomainService,
    TopicTypeService,
    DatePipe,
    {
      provide: APP_INITIALIZER,
      useFactory: loadAppConfig,
      deps: [CustomDomainService, AppAppearanceService, StemexeClientService], multi: true
    },
    httpInterceptorProviders,
    httpInterceptorProvidersForActivity,
    LoadingService,
    MatDatepickerModule,
    MatNativeDateModule,
    MsalService,
    // {
    //   provide: MAT_DATE_LOCALE, useValue:"en-UK"
    // },
    // {
    //   provide: OWL_DATE_TIME_LOCALE, useValue:"en-UK"
    // },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS_FACTORY]
    },
    { provide: MomentDateAdapter, useClass: CustomDateAdapter },
    {
      provide: RouteReuseStrategy, useClass: CacheRouteReuseStrategy
    },
    { provide: ErrorHandler, useClass: ChunkLoadErrorHandler },
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor() {
    registerLocaleData(localeAr, 'ar');
    registerLocaleData(localeFr, 'fr');
  }
}
